import { CdkMenuTrigger } from '@angular/cdk/menu';
import {
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
} from '@angular/core/rxjs-interop';
import { uniqueId, unionWith } from 'lodash';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import moment from 'moment';
import { Batch } from '../../models/batch';
import { Paginator } from '../../models/paginator';
import { IntegrationService } from '../../services/integration.service';
import { MessageService } from '../../services/message.service';

type BatchOption = Batch & { selected: boolean; disabled?: boolean };

@Component({
  selector: 'app-batch-select',
  templateUrl: './batch-select.component.html',
  styleUrls: ['./batch-select.component.scss'],
})
export class BatchSelectComponent {
  private readonly id = uniqueId('SELECT-');

  readonly integrationId = input.required<string>();
  readonly label = input<string>();
  readonly labelOutside = input(false);
  readonly search = input(false);
  readonly disabled = input(false);
  readonly emitOnInit = input(true);
  readonly hideWhenOnlyOneOption = input(false);

  readonly value = model<Batch | undefined>();
  readonly change = output<{ value?: Batch }>();

  readonly loading = signal(false);
  readonly batches = signal<Paginator<BatchOption>>({
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
  });
  readonly show = signal(false);
  readonly query = signal<string | undefined>(undefined);

  // derived instead of hand-synced
  readonly selected = computed<BatchOption[]>(() => {
    const v = this.value();
    return v ? [{ ...v, selected: true, disabled: false }] : [];
  });

  readonly selection = computed<string | undefined>(() => {
    const v = this.value();
    return v ? this.batchLabel(v) : undefined;
  });

  readonly canDisplay = computed<boolean>(() => {
    const hideWhenEmpty = this.hideWhenOnlyOneOption();
    const batches = this.batches();
    return hideWhenEmpty ? batches.docs.length > 1 : true;
  });

  // Prevents the newest batch being auto-selected again after a clear() or a
  // user-driven selection.
  private pristine = true;
  private requestToken = 0;

  private readonly queryUpdate = new Subject<string | undefined>();

  private readonly integrationService = inject(IntegrationService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly trigger = viewChild<CdkMenuTrigger>('trigger');

  constructor() {
    this.messageService.changeEmitted$.pipe(
      takeUntilDestroyed(this.destroyRef),
    )
      .subscribe(change => {
        if (change?.key === 'LAYOUT.SELECT' && change.data?.id !== this.id) {
          this.show.set(false);
        }
      });

    this.queryUpdate.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    )
      .subscribe(() => {
        this.resetPage();
        void this.retrieveBatches(true);
      });

    // (Re)load page 1 whenever the integration id changes (rxjs-first, like the
    // query subscription above; avoids a synchronous signal write inside an
    // effect
    toObservable(this.integrationId)
      .pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((id) => {
        this.resetPage();
        void this.retrieveBatches(
          true,
          id,
        );
      });
  }

  async retrieveBatches(
    replace = false,
    integrationId = this.integrationId(),
  ): Promise<void> {
    const token = ++this.requestToken;
    this.loading.set(true);
    try {
      const result = await this.integrationService.retrieveBatches(
        integrationId,
        this.batches().page,
        this.batches().limit,
        this.batches().offset,
        '-createdAt',
        this.query(),
      );

      // drop stale responses
      if (token !== this.requestToken) return;

      this.batches.set({
        ...result,
        docs: replace
          ? result.docs.map((batch) => ({
            ...batch,
            selected: this.isSelected(batch),
          }))
          : unionWith(
            this.batches().docs,
            result.docs.map((batch) => ({
              ...batch,
              selected: this.isSelected(batch),
            })),
            this.batchComparator,
          ),
      });
      this.syncFromValue();
    } catch (err) {
      if (token !== this.requestToken) return;
    } finally {
      if (token === this.requestToken) this.loading.set(false);
    }
  }

  // Only handles the pristine-autoselect case now; `selected`/`selection`
  // track `value` on their own via the computed()s above.
  private syncFromValue(): void {
    if (this.value()) return;
    if (this.pristine && this.emitOnInit() && this.batches().docs.length > 0) {
      this.select(this.batches().docs[0]);
    }
  }

  isSelected(option: Batch): boolean {
    return this.value()?._id === option._id;
  }

  batchComparator(batchA: Batch, batchB: Batch) {
    return batchA._id === batchB._id;
  }

  resetPage(): void {
    this.batches.update((list) => ({
      ...list,
      limit: 10,
      page: 1,
      offset: 0,
    }));
  }

  select(option: Batch): void {
    this.pristine = false;
    if (this.value()?._id === option._id) {
      this.trigger()?.close();
      return;
    }
    this.value.set(option);
    this.change.emit({ value: option });
    this.trigger()?.close();
  }

  onClear(): void {
    this.pristine = false;
    this.value.set(undefined);
    // Clearing the selection also drops an active search: refetch the full list
    const hadQuery = !!this.query();
    this.query.set(undefined);
    this.change.emit({ value: undefined });
    if (hadQuery) this.queryUpdate.next(undefined);
    this.trigger()?.close();
  }

  toggle(): void {
    this.show.update((value) => !value);
    if (this.show()) {
      this.messageService.emitChange(
        'LAYOUT',
        'SELECT',
        { id: this.id },
      );
    }
  }

  onQueryChange(): void {
    this.queryUpdate.next(this.query());
  }

  onScroll(): void {
    if (this.loading() || !this.batches().hasNextPage) return;
    this.batches.update((list) => ({
      ...list,
      offset: list.page * list.limit,
      page: list.page + 1,
    }));
    void this.retrieveBatches();
  }

  batchLabel(batch: Batch): string {
    const date = batch.createdAt ? moment(batch.createdAt)
      .format('YYYY-MM-DD HH:mm') : '';
    return `#${batch.number}${batch.status ? ` · ${batch.status}` : ''}${date
      ? ` · ${date}`
      : ''}`;
  }
}
